/**
 * Placing roping-arena equipment around an arena outline, following
 * Priefert's 150' x 250' layout drawing (ROPING7): the roping chute sits in
 * the end fence near the corner on the return-alley side, head and heel boxes
 * either side of it, and the steer alley runs back from the chute and turns
 * toward the return alley. The stripping chute is in the far end fence with
 * the catch pen behind it.
 */
import { ARENA_EQUIPMENT } from '../constants/furniture';
import { generateId } from './measurements';

const PX = 40;
const item = (id) => ARENA_EQUIPMENT.find(e => e.id === id);

// Arena-local frame: u across the width, v along the length (ft), from the center
const frame = (shape) => {
  const a = (shape.angleDeg * Math.PI) / 180;
  const ux = Math.cos(a), uy = Math.sin(a);
  return (u, v) => ({
    x: shape.center.x + (ux * u - uy * v) * PX,
    y: shape.center.y + (uy * u + ux * v) * PX,
  });
};

const place = (shape, toPlan, id, u, v, turnDeg, setup, roomId) => ({
  ...item(id),
  id: generateId(),
  templateId: id,
  ...toPlan(u, v),
  rotation: shape.angleDeg + turnDeg,
  arenaId: roomId,
  arenaSetup: setup,
});

export const ropingEndLayout = (shape, roomId) => {
  const toPlan = frame(shape);
  const hw = shape.widthFt / 2, hl = shape.lengthFt / 2;
  const r = shape.cornerRadiusFt || 0;
  const side = shape.alley?.side === 'left' ? -1 : 1;
  const chuteLen = item('rc22m-chute').height / 12;
  // Chute in the end fence, its front just inside the arena, near the alley-side corner
  const uChute = side * Math.max(0, hw - r - 16);
  const vChute = -hl + chuteLen / 2 - 1;
  const items = [place(shape, toPlan, 'rc22m-chute', uChute, vChute, 0, 'roping', roomId)];
  // Boxes either side, from the chute wall out about 12 ft, 18 ft deep into the arena.
  // Facing into the arena from behind the chute, the head box is on the left.
  const boxDepth = item('head-box').height / 12;
  const boxOffset = 1.5 + item('head-box').width / 12 / 2;
  items.push(place(shape, toPlan, 'head-box', uChute + boxOffset, -hl + boxDepth / 2, 0, 'roping', roomId));
  items.push(place(shape, toPlan, 'heel-box', uChute - boxOffset, -hl + boxDepth / 2, 0, 'roping', roomId));
  // Steer alley: three 8 ft sections straight back, then four across toward the return alley
  const back = vChute - chuteLen / 2;
  for (let k = 0; k < 3; k++) {
    items.push(place(shape, toPlan, 'steer-alley', uChute, back - 4 - 8 * k, 0, 'roping', roomId));
  }
  const turnV = back - 24 - 1.4;
  for (let k = 0; k < 4; k++) {
    items.push(place(shape, toPlan, 'steer-alley', uChute + side * (1.4 + 4 + 8 * k), turnV, 90, 'roping', roomId));
  }
  return items;
};

export const strippingEndLayout = (shape, roomId) => {
  const toPlan = frame(shape);
  const hw = shape.widthFt / 2, hl = shape.lengthFt / 2;
  const r = shape.cornerRadiusFt || 0;
  const side = shape.alley?.side === 'left' ? -1 : 1;
  const chuteLen = item('stripping-chute').height / 12;
  const pen = item('catch-pen');
  const uChute = side * Math.max(0, hw - r - 10);
  // Stripping chute through the far end fence, emptying into the catch pen behind it
  return [
    place(shape, toPlan, 'stripping-chute', uChute, hl + chuteLen / 2 - 3, 0, 'stripping', roomId),
    place(shape, toPlan, 'catch-pen', uChute, hl + chuteLen - 3 + pen.height / 12 / 2, 0, 'stripping', roomId),
  ];
};
